import type {
  Constructor,
  Driver,
  RaceResult,
  SeasonState,
  Standings,
  Team,
  Track,
  Weather,
} from "../types";
import { mixSeed, Rng } from "./rng";
import { historicAffinity } from "./synergy";
import {
  directorCarBonus,
  directorChassisAffinity,
  directorDevelopmentBonus,
  directorDnfDelta,
  directorLuckMult,
  directorPaceBonus,
  directorScoreBonus,
  directorSynergyMult,
  directorWetBonus,
} from "./director";
import {
  CALENDAR_SIZE,
  LUCK_CALM,
  LUCK_CHAOS,
  POINTS,
  WEATHER_HEAVY,
  WEATHER_LIGHT,
} from "../data/tracks";

/** Roll the weather for one race. Independent of track — any GP can be wet. */
export function rollWeather(rng: Rng): Weather {
  const r = rng.next();
  if (r < WEATHER_HEAVY) return "heavy_rain";
  if (r < WEATHER_HEAVY + WEATHER_LIGHT) return "light_rain";
  return "dry";
}

/**
 * Pre-draw the weather for every round of the calendar, up front and from the
 * seed (like the calendar and rivals) — so it's fixed, reroll-independent, and
 * can be shown to the player during the draft. Call after drawCalendar.
 */
export function drawWeather(rng: Rng, s: SeasonState): void {
  s.weather = s.calendar.map(() => rollWeather(rng));
}

// How much of the track's driverPace weight shifts onto rain skill in the wet.
const WET_SHARE: Record<Weather, number> = { dry: 0, light_rain: 0.3, heavy_rain: 0.6 };

// Fraction of the motivator/underdog synergy multiplier that also applies to the
// (already large) historic-drive bonus. Tuned so Wolff is the synergy specialist
// without turning the elite Mercedes rivals into auto-champions. See baseScore.
const MOTIVATOR_HISTORIC_AMP = 0.4;

/**
 * Luck-free strength of one car at a track: the weighted blend of chassis and
 * driver attributes (with synergy applied). This is the deterministic core of
 * the race score and is exposed for the season screen's projected-score panel
 * (which previews "dry" since the real weather isn't rolled until the race runs).
 */
export function baseScore(
  driver: Driver,
  team: Team,
  track: Track,
  weather: Weather = "dry",
  raceIndex = 0,
  totalRaces = CALENDAR_SIZE,
): number {
  const { weights } = track;
  // The historic-drive bonus is the driver's own — only the car they actually
  // raced earns it, never the teammate sharing the chassis.
  const affinity = historicAffinity(driver, team.chassis);
  const dir = team.director;
  // Home-machinery boost: amplifies the director's PERFORMANCE levers when the
  // chassis is from the constructor they ran. 1× otherwise.
  const aff = directorChassisAffinity(dir, team.chassis);
  // The director amplifies EXISTING synergy (motivator/underdog); it never
  // invents synergy, so a 1× multiplier on zero synergy stays zero. Affinity
  // scales only the boost ABOVE 1× — a 1× (no-op) multiplier stays 1×.
  // The motivator/underdog lever is already a MULTIPLIER, so home affinity does
  // NOT stack onto it (that multiplier-on-multiplier turned the Wolff+Mercedes
  // rivals — home boost × synergy mult × historic amp on an already-elite lineup
  // — into ~90%-when-present auto-champions). Affinity still scales the ADDITIVE
  // specialty levers (pace, wet, score, dev) via `aff` elsewhere; the synergy
  // amplification stands on its own.
  //
  // Asymmetry: only the PLAYER's director amplifies synergy. The two Mercedes
  // rival lineups are the only motivator (Wolff) rivals, and they already stack
  // Hamilton Prime + the best chassis + teammate synergy + both historic — giving
  // them the amplification too made them ~78%-when-present (vs ~52% without).
  // Withholding it from rivals keeps Wolff a top PLAYER pick (unchanged) while
  // leaving Mercedes a strong-but-beatable boss instead of an auto-champion.
  const synMult = team.isPlayer ? directorSynergyMult(dir, team) : 1;
  // A motivator/underdog also amplifies the on-brand historic-drive bonus (pace/
  // aero from a driver racing their old marque), but ONLY for a lineup with real
  // chemistry — nonzero team synergy means same-nation or ex-teammates. A merely
  // on-theme pair (both drove the marque but no shared chemistry, e.g. two
  // different-nation greats) gets no historic amplification, so the motivator
  // stays a synergy specialist instead of a blanket buff on any themed team.
  //
  // The historic bonus is already large (+10 pace/+4 aero PER qualifying driver),
  // so it's amplified only PARTIALLY (MOTIVATOR_HISTORIC_AMP): full synergy-mult
  // on it launched the already-elite Wolff+Mercedes rivals to ~90%-when-present.
  const histMult = team.synergy.consistency > 0 ? 1 + (synMult - 1) * MOTIVATOR_HISTORIC_AMP : 1;
  // A home-constructor director makes the car itself better (motor + aero).
  // Player-only, like the synergy amplification: rewards YOU for matching your
  // director to your chassis without re-inflating the (always-themed) rival grid
  // — every rival is on home machinery, so a symmetric buff would just raise the
  // whole field and tax non-themed player drafts.
  const carBonus = team.isPlayer ? directorCarBonus(dir, team.chassis) : { motor: 0, aero: 0 };
  const motor = team.chassis.motor + team.synergy.motor * synMult + carBonus.motor;
  const aero = team.chassis.aero + carBonus.aero;
  const pace = driver.pace + affinity.pace * histMult + directorPaceBonus(dir) * aff;
  const cons = driver.consistency + team.synergy.consistency * synMult + affinity.consistency * histMult;
  const rain = driver.rain;
  const wetShare = WET_SHARE[weather];
  // A rain master's wet edge is a DIRECT score bonus scaled by how wet it is
  // (0 dry / 0.3 light / 0.6 heavy) and by home-machinery affinity — NOT folded
  // into the rain attribute and diluted by the track's pace weight. So it lands
  // hard everywhere, even on low-pace circuits like Monaco. Inert in the dry.
  const wetEdge = directorWetBonus(dir) * wetShare * aff;
  return (
    weights.teamMotor * motor +
    weights.teamAero * aero +
    weights.driverPace * (1 - wetShare) * pace +
    weights.driverPace * wetShare * rain +
    weights.driverConsistency * cons +
    wetEdge +
    directorScoreBonus(dir) * aff +
    directorDevelopmentBonus(dir, raceIndex, totalRaces) * aff
  );
}

/**
 * Effective race score for one car: base strength × a luck multiplier. The
 * swing's random value is passed in (drawn by the caller) rather than drawn
 * here, so every car consumes the same number of randoms whether it finishes
 * or retires — keeping each car's luck pinned to its grid position.
 */
function entryScore(
  driver: Driver,
  team: Team,
  track: Track,
  luck: number,
  weather: Weather,
  raceIndex: number,
  totalRaces: number,
  swingRand: number,
): number {
  // The director widens (gambler / aggressive) or narrows (reliable) the swing.
  // Clamp so the multiplier can never flip the score negative.
  const effLuck = Math.min(0.9, Math.max(0, luck * directorLuckMult(team.director)));
  const swing = 1 + (swingRand * 2 - 1) * effLuck;
  return baseScore(driver, team, track, weather, raceIndex, totalRaces) * swing;
}

// Dry retirement risk: a flat floor every car carries, plus a consistency term
// (a sloppy driver adds up to DRY_CONS_K). Tuned so an average car (consistency
// ~84) retires ~13% of dry races — high enough that reliability and the engineer
// visibly matter, instead of the old ~2% where DNFs almost never happened.
const DRY_BASE = 0.17;
const DRY_CONS_K = 0.14;
// Extra risk pool added by wet weather, on top of the dry base. Rain skill
// discounts it (RAIN_MIT) but never erases it — even an ace can spin off in the
// wet. Measured field rates: ~20% light / ~24% heavy (vs ~13% dry).
const WET_DNF_POOL: Record<Weather, number> = { dry: 0, light_rain: 0.17, heavy_rain: 0.24 };
const RAIN_MIT = 0.6;

/**
 * Probability that one car retires (DNF) in the given weather, 0.01..0.6. Pure
 * (no RNG) so the UI can preview a director's effect on retirement risk before
 * the draft commits.
 */
export function dnfProbability(driver: Driver, team: Team, weather: Weather): number {
  const cons = Math.min(99, driver.consistency + team.synergy.consistency);
  const base = DRY_BASE + ((100 - cons) / 100) * DRY_CONS_K;
  // engineer trims the wet pool; reliability trims (or, when negative, raises)
  // the overall probability. Home-machinery affinity deepens the engineer's wet
  // cut, but leaves the reliability-driven flat term (a stable trait) alone.
  const { wetCut, flat } = directorDnfDelta(team.director);
  const aff = directorChassisAffinity(team.director, team.chassis);
  const wetPool = Math.max(0, WET_DNF_POOL[weather] - wetCut * aff);
  // Rain skill discounts the wet pool by at most RAIN_MIT (a 99-rain ace still
  // carries ~40% of it), so the wet always bites — it never confers immunity.
  const mitigation = (Math.min(99, driver.rain) / 100) * RAIN_MIT;
  return Math.min(0.6, Math.max(0.01, base + wetPool * (1 - mitigation) - flat));
}

function dnfRoll(rng: Rng, driver: Driver, team: Team, weather: Weather): boolean {
  return rng.next() < dnfProbability(driver, team, weather);
}

interface RawEntry {
  team: Team;
  driver: Driver;
  score: number;
  dnf: boolean;
}

/** Simulate one race. Each team fields 2 cars (both share the chassis). */
export function simRace(
  rng: Rng,
  teams: readonly Team[],
  track: Track,
  weather: Weather,
  luck: number,
  raceIndex = 0,
  totalRaces = CALENDAR_SIZE,
): RaceResult {
  const entries: RawEntry[] = [];
  for (const team of teams) {
    for (const driver of [team.d1, team.d2]) {
      const dnf = dnfRoll(rng, driver, team, weather);
      // Always draw the swing — even for a DNF — so each car consumes a fixed
      // count of randoms. That keeps every car's luck tied to its grid position,
      // independent of the player's lineup (which only changes the player's own
      // two cars' base scores, never the rivals' draws).
      const swing = rng.next();
      entries.push({
        team,
        driver,
        score: dnf ? -1 : entryScore(driver, team, track, luck, weather, raceIndex, totalRaces, swing),
        dnf,
      });
    }
  }
  const finishers = entries.filter((e) => !e.dnf).sort((a, b) => b.score - a.score);
  const dnfs = entries.filter((e) => e.dnf);
  const ordered = [...finishers, ...dnfs];
  const order = ordered.map((e, i) => {
    const points = e.dnf ? 0 : (POINTS[i] ?? 0);
    e.team.points += points;
    return {
      teamName: e.team.name,
      isPlayer: e.team.isPlayer,
      driverName: e.driver.name,
      driverTeam: e.driver.team,
      position: e.dnf ? null : i + 1,
      points,
      dnf: e.dnf,
    };
  });
  return { track, weather, order };
}

/** Run the next scheduled race; returns the result (also pushed to state). */
export function runNextRace(s: SeasonState): RaceResult | null {
  if (s.round >= s.calendar.length) return null;
  const track = s.calendar[s.round];
  if (!track) return null;
  const luck = s.chaos ? LUCK_CHAOS : LUCK_CALM;
  // Race luck runs on its OWN RNG, seeded from (season seed + round). The draft
  // stream never touches it, so rerolls — and reloads — can't shift the luck;
  // the same date always produces the same race, and only the player's lineup
  // changes the outcome. Weather is pre-drawn (drawWeather); the ?? branch is a
  // fallback for older saves resumed mid-season without pre-drawn weather.
  const raceRng = new Rng(mixSeed(s.seed, s.round));
  const weather = s.weather[s.round] ?? rollWeather(new Rng(mixSeed(s.seed, s.round) ^ 0x9e37));
  // s.round is the 0-based index of the race being run (incremented below), and
  // drives the directors' season-long development ramp.
  const result = simRace(raceRng, s.opponents, track, weather, luck, s.round, s.calendar.length);
  s.results.push(result);
  s.round++;
  if (s.round >= s.calendar.length) {
    s.standings = computeStandings(s);
  }
  return result;
}

export function computeStandings(s: SeasonState): Standings {
  const byDriver = new Map<
    string,
    { driverName: string; driverTeam: Constructor; teamName: string; isPlayer: boolean; points: number }
  >();
  for (const race of s.results) {
    for (const e of race.order) {
      const key = `${e.driverName}|${e.driverTeam}|${e.teamName}`;
      const existing = byDriver.get(key);
      if (existing) {
        existing.points += e.points;
      } else {
        byDriver.set(key, {
          driverName: e.driverName,
          driverTeam: e.driverTeam,
          teamName: e.teamName,
          isPlayer: e.isPlayer,
          points: e.points,
        });
      }
    }
  }
  const drivers = [...byDriver.values()].sort((a, b) => b.points - a.points);
  const constructors = [...s.opponents].sort((a, b) => b.points - a.points);
  return { constructors, drivers };
}
